// money.js — ONE home for dollar math and dollar display (2026-10-14).
//
// A tester's Cash Flow Calendar showed "$49.980000000000004" for two
// subscriptions ending in .99: the day total was a raw float sum dropped into a
// template literal. Two rules now, enforced here and tested:
//   1. SUM IN CENTS. sumMoney adds integer cents and returns dollars, so
//      24.99 + 24.99 is exactly 49.98. roundMoney snaps any figure to cents.
//   2. ONE FORMATTER. Every amount on screen goes through fmtUSD / fmtUSDExact /
//      fmtUSDCompact, which round to cents BEFORE formatting and never emit more
//      than two decimals. A raw float cannot reach the screen through them.
// Pure, no imports; usable from src/ and api/ (api imports src/lib/* directly).

const toCents = (n) => Math.round((Number(n) || 0) * 100);

/** Snap to cents: 49.980000000000004 -> 49.98. */
export const roundMoney = (n) => toCents(n) / 100;

/** Exact dollar total of a list (or of a mapped field): integer-cent arithmetic. */
export function sumMoney(values, pick = null) {
  let cents = 0;
  for (const v of values || []) cents += toCents(pick ? pick(v) : v);
  return cents / 100;
}

/** USD with the sign OUTSIDE the dollar sign (-$1,449, never $-1,449), rounded
 *  to cents first, then to `decimals` places (0 = whole dollars, max 2). */
export function fmtUSD(n, decimals = 0) {
  const d = Math.max(0, Math.min(2, Number(decimals) || 0));
  const num = roundMoney(n);
  const abs = Math.abs(num);
  return (num < 0 ? "-$" : "$") + abs.toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d });
}

/** Whole dollars stay clean ($60), anything with cents shows both digits ($49.98). */
export function fmtUSDExact(n) {
  const num = roundMoney(n);
  return fmtUSD(num, Number.isInteger(num) ? 0 : 2);
}

/** Tight label for calendar cells and chips: $49.98, $410, $1.2k, $12k. Never
 *  more than two decimals, never wider than ~7 characters. */
export function fmtUSDCompact(n) {
  const num = roundMoney(n);
  const abs = Math.abs(num);
  const sign = num < 0 ? "-" : "";
  if (abs >= 10000) return `${sign}$${Math.round(abs / 1000)}k`;
  if (abs >= 1000) return `${sign}$${(abs / 1000).toFixed(1).replace(/\.0$/, "")}k`;
  return fmtUSDExact(num);
}

/** Seven equal columns that can never widen: minmax(0,1fr) lets a long label
 *  shrink or truncate instead of stretching its column (the calendar bug). */
export const SEVEN_EQUAL_COLUMNS = "repeat(7,minmax(0,1fr))";
